/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { IGiftCardExtend } from '@msdyn365-commerce/global-state';
import { INodeProps } from '@msdyn365-commerce-modules/utilities';

import { getItem, IItem } from './get-item';

interface IGetListResources {
    enteredGiftCardLabel: string;
    giftCardAvailableBalanceLabel: string;
    removeGiftCardButton: string;
    externalGiftCardAppliedLabel: string;
    externalGiftCardBalanceLabel: string;
    externalGiftCardTotalAppliedLabel: string;
}

interface IGetListInput {
    giftCards?: IGiftCardExtend[];
    canRemove: boolean;
    amountDue?: number;
    resources: IGetListResources;
    getFormattedPrice(price?: number, currencyCode?: string): string;
    onRemoveGiftCard(giftCardNumber: string): void;
}

export interface IList {
    listProps: INodeProps;
    list: IItem[];
    headerData: {
        headerGiftCardLabel: string;
        headerGiftCardBalanceLabel: string;
        headerGiftCardRemoveLabel: string;
    };
    totalData: {
        totalAppliedGiftCardLabel: string;
        totalApplied: string;
    };
}

export const getList = ({
    giftCards,
    canRemove,
    resources,
    getFormattedPrice,
    onRemoveGiftCard,
    amountDue
}: IGetListInput): IList | undefined => {
    if (!giftCards || giftCards.length === 0) {
        return undefined;
    }

    const giftCardTotalApplied = giftCards.reduce((sum, gc) => sum + (gc.Balance ?? 0), 0);

    const totalApplied = Number(Math.min(giftCardTotalApplied, amountDue ?? 0).toFixed(2));

    const totalAppliedFormatted = getFormattedPrice(totalApplied, giftCards[0]?.CardCurrencyCode);

    const listProps = {
        className: 'cus-external-gift-card__list'
    };

    const itemResources = {
        enteredGiftCardLabel: resources.enteredGiftCardLabel,
        giftCardAvailableBalanceLabel: resources.giftCardAvailableBalanceLabel,
        removeGiftCardButton: resources.removeGiftCardButton
    };

    const list = giftCards.map(giftCard =>
        getItem({
            canRemove,
            getFormattedPrice,
            giftCard,
            onRemoveGiftCard,
            resources: itemResources
        })
    );

    const headerData = {
        headerGiftCardLabel: resources.externalGiftCardAppliedLabel,
        headerGiftCardBalanceLabel: resources.externalGiftCardBalanceLabel,
        headerGiftCardRemoveLabel: resources.removeGiftCardButton
    };

    const totalData = {
        totalAppliedGiftCardLabel: resources.externalGiftCardTotalAppliedLabel,
        totalApplied: totalAppliedFormatted
    };

    return {
        listProps,
        list,
        headerData,
        totalData
    };
};
